import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from './i18n/LanguageContext';

// Word type colors for sci-fi UI
const TYPE_COLORS = {
  verb: { bg: 'from-blue-500 to-blue-600', text: 'text-blue-100', badge: 'bg-blue-500', dark: 'from-blue-600 to-blue-800' },
  noun: { bg: 'from-emerald-500 to-emerald-600', text: 'text-emerald-100', badge: 'bg-emerald-500', dark: 'from-emerald-600 to-emerald-800' },
  preposition: { bg: 'from-amber-500 to-amber-600', text: 'text-amber-100', badge: 'bg-amber-500', dark: 'from-amber-600 to-amber-800' },
  adjective: { bg: 'from-purple-500 to-purple-600', text: 'text-purple-100', badge: 'bg-purple-500', dark: 'from-purple-600 to-purple-800' },
  pronoun: { bg: 'from-teal-500 to-teal-600', text: 'text-teal-100', badge: 'bg-teal-500', dark: 'from-teal-600 to-teal-800' },
  particle: { bg: 'from-rose-500 to-rose-600', text: 'text-rose-100', badge: 'bg-rose-500', dark: 'from-rose-600 to-rose-800' },
  expression: { bg: 'from-pink-500 to-pink-600', text: 'text-pink-100', badge: 'bg-pink-500', dark: 'from-pink-600 to-pink-800' },
};
const DEFAULT_COLOR = { bg: 'from-gray-500 to-gray-600', text: 'text-gray-100', badge: 'bg-gray-500', dark: 'from-gray-600 to-gray-800' };

const DEFAULT_TABS = [
  { id: 'decoder', labelKey: 'decoder_tab_decode', icon: '🔍' },
  { id: 'activity', labelKey: 'decoder_tab_activity', icon: '📋' },
  { id: 'vocabulary', labelKey: 'decoder_tab_vocabulary', icon: '📚' },
  { id: 'help', labelKey: 'decoder_tab_help', icon: '📖' },
];

const DarijaDecoder = ({ API, showNotification, isDarkMode, userToken, currentUser, hasPaidPlan, setCurrentView, setInitialProfileTab }) => {
  const { t } = useTranslation();

  // App-shell caches: read from localStorage synchronously so the page
  // renders config + help sections from frame 1 instead of showing a blocking
  // spinner then popping into the real layout.
  const _readCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  const _cachedDecoderConfig = _readCache('decoderConfigCache');
  const _cachedHelpSections = _readCache('decoderHelpCache');
  const _cachedUnreadCount = _readCache('decoderUnreadCountCache');
  const _cachedVocabulary = _readCache('decoderVocabularyCache');
  const _cachedHistory = _readCache('decoderHistoryCache');
  const _cachedCorrections = _readCache('decoderCorrectionsCache');

  const [inputText, setInputText] = useState('');
  const [result, setResult] = useState(null);
  const [isDecoding, setIsDecoding] = useState(false);
  const [decodingStep, setDecodingStep] = useState(0);
  const [visibleWords, setVisibleWords] = useState(0);
  const [activeView, setActiveView] = useState('decoder');
  const [history, setHistory] = useState(_cachedHistory || []);
  const [corrections, setCorrections] = useState(_cachedCorrections || []);
  const [unreadCount, setUnreadCount] = useState(_cachedUnreadCount || 0);
  const [vocabulary, setVocabulary] = useState(_cachedVocabulary || []);
  const [reportText, setReportText] = useState('');
  const abortControllerRef = useRef(null);
  const [showReport, setShowReport] = useState(false);
  const [helpSections, setHelpSections] = useState(_cachedHelpSections || []);
  const [configLoaded, setConfigLoaded] = useState(!!_cachedDecoderConfig);
  // All config from backend — no hardcoded numbers
  const [decoderConfig, setDecoderConfig] = useState(_cachedDecoderConfig || {
    max_input_length: 500,
    rate_limit_per_hour: 20,
    max_history_per_user: 50,
    max_vocabulary_per_user: 100,
    disclaimer_visible: false,
    disclaimer_text: '',
    beta_badge_visible: true,
    beta_badge_text: 'Beta / Experimental'
  });

  // Tab labels always use translation. Admin can still customize tab icons.
  const TABS = DEFAULT_TABS.map(tab => {
    if (tab.id === 'activity') return { ...tab, label: t(tab.labelKey), icon: decoderConfig.tab_activity_icon_url ? null : tab.icon, icon_url: decoderConfig.tab_activity_icon_url || '' };
    if (tab.id === 'vocabulary') return { ...tab, label: t(tab.labelKey), icon: decoderConfig.tab_vocabulary_icon_url ? null : tab.icon, icon_url: decoderConfig.tab_vocabulary_icon_url || '' };
    if (tab.id === 'help') return { ...tab, label: t(tab.labelKey), icon: decoderConfig.tab_help_icon_url ? null : tab.icon, icon_url: decoderConfig.tab_help_icon_url || '' };
    return { ...tab, label: t(tab.labelKey) };
  });

  const headers = { 'Authorization': `Bearer ${userToken}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchHelp();
    fetchDecoderConfig();
    if (!userToken) return;
    fetchUnreadCount();
    fetchVocabulary();
  }, [userToken]);

  const fetchHelp = async () => {
    try {
      const res = await fetch(`${API}/decoder/help`);
      if (res.ok) { const d = await res.json(); const sections = d.sections || []; setHelpSections(sections); try { localStorage.setItem('decoderHelpCache', JSON.stringify(sections)); } catch {} }
    } catch (e) { /* silent */ }
  };

  const fetchDecoderConfig = async () => {
    try {
      const res = await fetch(`${API}/decoder/settings`);
      if (res.ok) { const d = await res.json(); setDecoderConfig(prev => { const merged = {...prev, ...d}; try { localStorage.setItem('decoderConfigCache', JSON.stringify(merged)); } catch {} return merged; }); }
    } catch (e) { /* silent */ }
    setConfigLoaded(true);
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await fetch(`${API}/decoder/my-corrections/unread`, { headers });
      if (res.ok) { const d = await res.json(); const n = d.unread_count || 0; setUnreadCount(n); try { localStorage.setItem('decoderUnreadCountCache', JSON.stringify(n)); } catch {} }
    } catch (e) { /* silent */ }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/decoder/history`, { headers });
      if (res.ok) { const d = await res.json(); const h = d.history || []; setHistory(h); try { localStorage.setItem('decoderHistoryCache', JSON.stringify(h)); } catch {} }
    } catch (e) { /* silent */ }
  };

  const fetchCorrections = async () => {
    try {
      const res = await fetch(`${API}/decoder/my-corrections`, { headers });
      if (res.ok) { const d = await res.json(); const c = d.corrections || []; setCorrections(c); try { localStorage.setItem('decoderCorrectionsCache', JSON.stringify(c)); } catch {} }
    } catch (e) { /* silent */ }
  };

  const fetchVocabulary = async () => {
    try {
      const res = await fetch(`${API}/decoder/vocabulary`, { headers });
      if (res.ok) { const d = await res.json(); const v = d.words || []; setVocabulary(v); try { localStorage.setItem('decoderVocabularyCache', JSON.stringify(v)); } catch {} }
    } catch (e) { /* silent */ }
  };

  const handleDecode = async () => {
    if (!inputText.trim() || inputText.trim().length < 2) {
      showNotification(`Please enter at least 2 characters (max ${decoderConfig.max_input_length})`, 'warning');
      return;
    }
    if (!userToken) {
      showNotification(t('decoder_login_required'), 'info');
      return;
    }

    setIsDecoding(true);
    setDecodingStep(1);
    setVisibleWords(0);
    setResult(null);

    // Create AbortController for cancel support
    const controller = new AbortController();
    abortControllerRef.current = controller;

    await new Promise(r => setTimeout(r, 800));
    if (controller.signal.aborted) return;
    setDecodingStep(2);

    try {
      const res = await fetch(`${API}/decoder/decode`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ text: inputText.trim() }),
        signal: controller.signal
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 401) {
          throw new Error('Your session has expired. Please log in again to continue.');
        }
        if (res.status === 429) {
          throw new Error('You\'ve reached the decode limit for this hour. Please wait a bit and try again.');
        }
        if (res.status === 403) {
          throw new Error('Darija Decoder requires a Pro subscription. Upgrade to unlock this feature!');
        }
        throw new Error(err.detail || 'Decode failed');
      }

      const data = await res.json();

      // Handle "not found" when AI is disabled and phrase not in knowledge base
      if (data.source === 'not_found') {
        showNotification(data.message || t('decoder_phrase_not_found'), 'info');
        setDecodingStep(0);
        setIsDecoding(false);
        return;
      }

      setResult(data.result);
      setDecodingStep(3);

      const words = data.result?.result?.words || [];
      for (let i = 0; i < words.length; i++) {
        await new Promise(r => setTimeout(r, 300));
        setVisibleWords(i + 1);
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        // User cancelled — already reset by handleCancel
        return;
      }
      showNotification(e.message || 'Failed to decode', 'error');
      setDecodingStep(0);
    }
    setIsDecoding(false);
    abortControllerRef.current = null;
  };

  const handleCancel = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = null;
    setIsDecoding(false);
    setDecodingStep(0);
    showNotification(t('decoder_decode_cancelled'), 'info');
  };

  const handleSaveWord = async (word) => {
    try {
      const res = await fetch(`${API}/decoder/vocabulary/save`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          word: word.word,
          latin: word.latin,
          arabic: word.arabic,
          meaning: word.meaning,
          type: word.type,
          source_text: inputText
        })
      });
      if (res.ok) {
        showNotification(t('decoder_word_saved'), 'success');
        fetchVocabulary();
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || t('decoder_save_failed'), 'warning');
      }
    } catch (e) { showNotification(t('decoder_save_failed'), 'error'); }
  };

  const handleReport = async () => {
    if (!reportText.trim()) return;
    try {
      const res = await fetch(`${API}/decoder/report`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          text: inputText,
          result_id: result?.id || '',
          message: reportText
        })
      });
      if (res.ok) {
        showNotification(t('decoder_report_submitted'), 'success');
        setShowReport(false);
        setReportText('');
      }
    } catch (e) { showNotification('Failed to submit report', 'error'); }
  };

  const markRead = async (id) => {
    try {
      await fetch(`${API}/decoder/my-corrections/${id}/read`, {
        method: 'PUT', headers
      });
      fetchCorrections();
      fetchUnreadCount();
    } catch (e) { /* silent */ }
  };

  const deleteVocabWord = async (id) => {
    try {
      await fetch(`${API}/decoder/vocabulary/${id}`, {
        method: 'DELETE', headers
      });
      fetchVocabulary();
    } catch (e) { /* silent */ }
  };

  const getTypeColor = (type) => TYPE_COLORS[type?.toLowerCase()] || DEFAULT_COLOR;

  const clear = () => {
    setResult(null);
    setDecodingStep(0);
    setVisibleWords(0);
    setInputText('');
    setShowReport(false);
  };

  const handleTabSwitch = (tabId) => {
    setActiveView(tabId);
    if (tabId === 'activity') { fetchHistory(); fetchCorrections(); }
    if (tabId === 'vocabulary') { fetchVocabulary(); }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const showProGlass = decoderConfig.is_pro_only && !hasPaidPlan;

  useEffect(() => {
    if (!configLoaded || !showProGlass || result) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/decoder/demo`);
        if (!res.ok || cancelled) return;
        const d = await res.json();
        if (!d || !d.result) return;
        setResult(d.result);
        setDecodingStep(3);
        const wordCount = (d.result?.result?.words?.length) || 0;
        setVisibleWords(wordCount);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [configLoaded, showProGlass, API, result]);

  // ── RENDER ─────────────────────────────────────────────────────────────────

  // Wait for config to load before rendering anything (prevents flash of content)
  if (!configLoaded) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!userToken) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center p-8">
          {decoderConfig.decoder_icon_url && <img src={`${API.replace('/api', '')}${decoderConfig.decoder_icon_url}`} alt="" className="w-16 h-16 object-contain mx-auto mb-4" />}
          <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('decoder_title')}</h2>
          <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('decoder_login_required')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pb-24 relative ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 backdrop-blur-md ${isDarkMode ? 'bg-gray-900/90 border-gray-700' : 'bg-white/90 border-gray-200'} border-b`}>
        <div className="max-w-2xl mx-auto px-4 pt-3 pb-4">
          {/* Top row: back + beta badge */}
          <div className="flex items-center justify-between mb-2">
            {setCurrentView ? (
              <button onClick={() => setCurrentView('translator')}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-medium transition-all ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('decoder_back')}
              </button>
            ) : <div />}
            {decoderConfig.beta_badge_visible && decoderConfig.beta_badge_text && (
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${
                isDarkMode ? 'bg-amber-900/40 text-amber-300 border border-amber-700' : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}>
                {decoderConfig.beta_badge_text}
              </span>
            )}
          </div>

          {/* Title */}
          <div className="flex items-center justify-center gap-2 mb-5">
            {decoderConfig.decoder_icon_url && <img src={`${API.replace('/api', '')}${decoderConfig.decoder_icon_url}`} alt="" className="w-7 h-7 object-contain" />}
            <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('decoder_title')}</h1>
          </div>

          {/* Tab Navigation — Modern card style on mobile, inline pills on desktop */}
          <div className="grid grid-cols-4 gap-2 sm:flex sm:justify-center sm:gap-2">
            {TABS.map(tab => {
              const isActive = activeView === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabSwitch(tab.id)}
                  className={`relative flex flex-col sm:flex-row items-center gap-1 sm:gap-1.5 py-2.5 sm:px-3 sm:py-2 font-medium whitespace-nowrap rounded-xl sm:rounded-lg transition-all ${
                    isActive
                      ? `${isDarkMode ? 'bg-purple-900/50 border-purple-500' : 'bg-purple-50 border-purple-300'} border sm:border-0 sm:bg-purple-600 sm:text-white sm:shadow-md`
                      : `${isDarkMode ? 'bg-gray-800/60 border-gray-700 hover:bg-gray-800' : 'bg-white border-gray-200 hover:bg-gray-50'} border sm:border-0 sm:bg-transparent`
                  }`}
                >
                  <div className={`w-10 h-10 sm:w-auto sm:h-auto rounded-xl sm:rounded-none flex items-center justify-center transition-all ${
                    isActive
                      ? `${isDarkMode ? 'bg-purple-600' : 'bg-purple-600'} text-white shadow-md sm:bg-transparent sm:shadow-none sm:text-white`
                      : `${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} sm:bg-transparent ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`
                  }`}>
                    {tab.icon_url
                      ? <img src={`${API.replace('/api', '')}${tab.icon_url}`} alt="" className="w-5 h-5 sm:w-5 sm:h-5 object-contain" />
                      : tab.icon && <span className="text-lg sm:text-base">{tab.icon}</span>}
                  </div>
                  <span className={`text-[10px] sm:text-sm leading-tight ${
                    isActive
                      ? `font-bold ${isDarkMode ? 'text-purple-300' : 'text-purple-700'} sm:text-white`
                      : `font-medium ${isDarkMode ? 'text-white' : 'text-gray-500'} ${isDarkMode ? 'sm:text-white' : 'sm:text-current'}`
                  }`}>{tab.label}</span>
                  {tab.id === 'activity' && unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 sm:static sm:ml-1 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center shadow-sm">{unreadCount}</span>
                  )}
                  {tab.id === 'vocabulary' && vocabulary.length > 0 && (
                    <span className={`absolute -top-1 -right-1 sm:static sm:ml-1 text-[10px] sm:text-xs px-1.5 sm:px-1.5 py-0.5 rounded-full shadow-sm ${
                      isActive ? 'bg-purple-500 text-white' : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-300 text-gray-600'
                    }`}>{vocabulary.length}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-3 sm:px-4 pt-4">

        {/* ── HOW IT WORKS VIEW ── */}
        {activeView === 'help' && (
          <div className="space-y-4">
            <div className="text-center mb-2">
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('decoder_tab_help')}</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('decoder_help_subtitle')}</p>
            </div>
            {helpSections.length === 0 ? (
              <div className={`text-center py-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {decoderConfig.tab_help_icon_url
                  ? <img src={`${API.replace('/api', '')}${decoderConfig.tab_help_icon_url}`} alt="" className="w-10 h-10 object-contain mx-auto mb-3" />
                  : <span className="text-4xl block mb-3">📖</span>}
                <p>{t('decoder_info_coming_soon')}</p>
              </div>
            ) : (
              helpSections.map((section, idx) => (
                <div key={section.id || idx} className={`p-5 rounded-2xl border ${
                  isDarkMode ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
                } shadow-sm`}>
                  <div className="flex items-start gap-3">
                    {section.icon_url
                      ? <img src={`${API.replace('/api', '')}${section.icon_url}`} alt="" className="w-7 h-7 object-contain mt-0.5" />
                      : <span className="text-2xl mt-0.5">{section.icon || '📝'}</span>}
                    <div className="flex-1">
                      <h3 className={`text-base font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {section.title}
                      </h3>
                      <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {section.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ── VOCABULARY VIEW ── */}
        {activeView === 'vocabulary' && (
          <div className="space-y-3">
            <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('decoder_my_word_collection')} ({vocabulary.length})
            </h2>
            {vocabulary.length === 0 ? (
              <p className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {t('decoder_vocabulary_empty')}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {vocabulary.map(w => {
                  const tc = getTypeColor(w.type);
                  return (
                    <div key={w.id} className={`p-2.5 sm:p-3 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                      <div className="flex justify-between items-start">
                        <div className="min-w-0 flex-1">
                          <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full text-white ${tc.badge}`}>{w.type}</span>
                          <p className={`text-base sm:text-lg font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`} dir="auto">{w.arabic || w.word}</p>
                          <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{w.latin}</p>
                          <p className={`text-xs sm:text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{w.meaning}</p>
                        </div>
                        <button onClick={() => deleteVocabWord(w.id)} className="text-red-400 hover:text-red-500 text-sm p-1 flex-shrink-0">x</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── ACTIVITY VIEW ── */}
        {activeView === 'activity' && (
          <div className="space-y-4">
            {/* Corrections */}
            {corrections.length > 0 && (
              <div>
                <h3 className={`text-sm font-bold mb-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                  {t('decoder_corrections_from_admin')}
                </h3>
                {corrections.map(c => (
                  <div key={c.id} className={`p-3 mb-2 rounded-xl border-l-4 ${
                    c.read
                      ? isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'
                      : isDarkMode ? 'bg-yellow-900/30 border-yellow-500' : 'bg-yellow-50 border-yellow-400'
                  }`}>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>"{c.original_text}"</p>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.correction_note}</p>
                    {c.expires_at && (
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {t('expires')}: {new Date(c.expires_at).toLocaleString()}
                      </p>
                    )}
                    {!c.read && (
                      <button onClick={() => markRead(c.id)} className="text-xs text-blue-500 mt-1">{t('decoder_mark_as_read')}</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* History */}
            <div>
              <h3 className={`text-sm font-bold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('decoder_recent_decodes')}</h3>
              {history.length === 0 ? (
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('decoder_no_decodes_yet')}</p>
              ) : (
                history.map(h => (
                  <button
                    key={h.id}
                    onClick={() => { setInputText(h.text); setActiveView('decoder'); }}
                    className={`w-full text-left p-2 mb-1 rounded-lg text-sm transition-all ${
                      isDarkMode ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <span className="font-medium" dir="auto">{h.text}</span>
                    <span className={`text-xs ml-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                      {timeAgo(h.created_at)}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── DECODER VIEW ── */}
        {activeView === 'decoder' && (
          <div className="space-y-4">

            {/* Input Area */}
            <div className={`relative rounded-2xl overflow-hidden transition-all duration-500 ${
              decodingStep === 1 ? 'ring-2 ring-purple-500 ring-opacity-75 animate-pulse' : ''
            }`}>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={t('decoder_input_placeholder')}
                dir="auto"
                maxLength={decoderConfig.max_input_length}
                rows={3}
                className={`w-full p-3 sm:p-4 text-base sm:text-lg rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all ${
                  isDarkMode ? 'bg-gray-800 text-white placeholder-gray-500 border-gray-700' : 'bg-white text-gray-900 placeholder-gray-400 border-gray-200'
                } border`}
              />
              <div className={`absolute bottom-2 right-3 text-xs ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}>
                {inputText.length}/{decoderConfig.max_input_length}
              </div>
            </div>

            {/* Decode Button */}
            <div className="flex gap-2">
              <button
                onClick={handleDecode}
                disabled={isDecoding || !inputText.trim()}
                className={`flex-1 py-3 rounded-xl font-bold text-white transition-all transform hover:scale-[1.02] ${
                  isDecoding
                    ? 'bg-gradient-to-r from-purple-400 to-indigo-400 cursor-wait'
                    : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500'
                } disabled:opacity-50 disabled:cursor-not-allowed shadow-lg`}
              >
                {isDecoding ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    {decodingStep === 1 ? t('decoder_scanning') : t('decoder_analyzing')}
                  </span>
                ) : `🔍 ${t('decoder_tab_decode')}`}
              </button>
              {isDecoding && (
                <button onClick={handleCancel}
                  className="px-4 py-3 rounded-xl font-medium transition-all bg-red-500 hover:bg-red-600 text-white"
                >
                  {t('cancel')}
                </button>
              )}
              {result && !isDecoding && (
                <button onClick={clear}
                  className={`px-4 py-3 rounded-xl font-medium transition-all ${
                    isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {t('decoder_clear')}
                </button>
              )}
            </div>

            {/* Scanning Animation */}
            {decodingStep >= 1 && decodingStep < 3 && (
              <div className="relative h-1 rounded-full overflow-hidden bg-gray-200">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 animate-scan rounded-full"></div>
              </div>
            )}

            {/* Results */}
            {result && decodingStep === 3 && (
              <div className="space-y-3 animate-fadeIn">
                {/* Full meaning */}
                <div className={`p-3 sm:p-4 rounded-2xl ${
                  isDarkMode ? 'bg-gray-800/80 border border-gray-700' : 'bg-white border border-gray-200'
                } shadow-lg`}>
                  <p className={`text-[10px] sm:text-xs font-medium mb-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    {result.result?.is_verified ? t('decoder_verified_translation') : t('decoder_full_meaning')}
                  </p>
                  <p className={`text-base sm:text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {result.result?.full_meaning}
                  </p>
                  {result.result?.context_note && (
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      💡 {result.result.context_note}
                    </p>
                  )}
                </div>

                {/* Word-by-word breakdown */}
                <div className="space-y-2">
                  <p className={`text-xs font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('decoder_word_by_word')} ({result.result?.words?.length || 0} {t('decoder_words')})
                  </p>

                  {result.result?.words?.map((word, idx) => {
                    const tc = getTypeColor(word.type);
                    const isVisible = idx < visibleWords;

                    return (
                      <div
                        key={idx}
                        className={`transform transition-all duration-500 ${
                          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                        }`}
                        style={{ transitionDelay: `${idx * 100}ms` }}
                      >
                        <div className={`p-3 sm:p-4 rounded-xl border ${
                          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        } shadow-sm hover:shadow-md transition-shadow`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full text-white font-medium ${tc.badge}`}>
                                  {word.type}
                                </span>
                                <span className={`text-[10px] sm:text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {idx + 1}/{result.result.words.length}
                                </span>
                              </div>
                              <p className={`text-xl sm:text-2xl font-bold mb-0.5 ${isDarkMode ? 'text-white' : 'text-gray-900'}`} dir="auto">
                                {word.arabic || word.word}
                              </p>
                              <p className={`text-xs sm:text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {word.latin}
                              </p>
                              <p className={`text-sm sm:text-base font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                {word.meaning}
                              </p>
                              {word.note && (
                                <p className={`text-[11px] sm:text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {word.note}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => handleSaveWord(word)}
                              className={`ml-2 p-1.5 sm:p-2 rounded-lg transition-all hover:scale-110 ${
                                isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-400'
                              }`}
                              title="Save to vocabulary"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Grammar note */}
                {result.result?.grammar_note && (
                  <div className={`p-3 rounded-xl text-sm ${
                    isDarkMode ? 'bg-indigo-900/30 text-indigo-300 border border-indigo-800' : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                  }`}>
                    <span className="font-bold">{t('decoder_grammar')}</span> {result.result.grammar_note}
                  </div>
                )}

                {/* Report button */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowReport(!showReport)}
                    className={`text-sm px-3 py-1.5 rounded-lg transition-all ${
                      isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {t('decoder_report_button')}
                  </button>
                </div>

                {/* Report form */}
                {showReport && (
                  <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <textarea
                      value={reportText}
                      onChange={(e) => setReportText(e.target.value)}
                      placeholder={t('decoder_report_placeholder')}
                      rows={3}
                      maxLength={decoderConfig.max_input_length}
                      className={`w-full p-3 rounded-lg resize-none text-sm focus:outline-none focus:ring-2 focus:ring-red-400 ${
                        isDarkMode ? 'bg-gray-900 text-white border-gray-600' : 'bg-gray-50 text-gray-900 border-gray-200'
                      } border`}
                    />
                    <div className="flex gap-2 mt-2">
                      <button onClick={handleReport}
                        className="px-4 py-1.5 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-all"
                      >
                        {t('decoder_submit_report')}
                      </button>
                      <button onClick={() => { setShowReport(false); setReportText(''); }}
                        className={`px-4 py-1.5 text-sm rounded-lg ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        )}

        {/* Spacer for fixed disclaimer */}
        {decoderConfig.disclaimer_visible && decoderConfig.disclaimer_text && (
          <div className="h-12"></div>
        )}
      </div>

      {/* Sticky disclaimer at bottom — above mobile nav */}
      {decoderConfig.disclaimer_visible && decoderConfig.disclaimer_text && (
        <div className={`fixed bottom-16 md:bottom-0 left-0 right-0 z-10 px-4 py-2.5 text-center border-t ${
          isDarkMode ? 'bg-gray-800/95 border-gray-700 backdrop-blur-sm' : 'bg-gray-50/95 border-gray-200 backdrop-blur-sm'
        }`}>
          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            ⓘ {decoderConfig.disclaimer_text}
          </p>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes scan {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .animate-scan {
          animation: scan 1.5s ease-in-out infinite;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {showProGlass && (
        <>
          <div className="absolute inset-0 z-20 cursor-not-allowed"
               style={{ backdropFilter: 'blur(0.6px)', WebkitBackdropFilter: 'blur(0.6px)',
                        backgroundColor: isDarkMode ? 'rgba(17,24,39,0.08)' : 'rgba(255,255,255,0.06)' }} />
          <div className="fixed z-30 pointer-events-none px-4" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(90vw, 28rem)' }}>
            <div className={`pointer-events-auto text-center p-8 max-w-md rounded-3xl shadow-2xl ring-4 ring-white/30 ${isDarkMode ? 'bg-gray-900/95' : 'bg-white/95'}`}>
              <span className="text-6xl mb-4 block">🔍</span>
              <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('decoder_title')}</h2>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('decoder_pro_only_message')}
              </p>
              <button
                onClick={() => { if (setInitialProfileTab) setInitialProfileTab('membership'); if (setCurrentView) setCurrentView('profile'); }}
                className="px-6 py-3 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold shadow-2xl hover:scale-105 transition-all flex items-center gap-2 mx-auto cursor-pointer"
              >
                <span className="text-xl">🔓</span><span>{t('upgrade_to_pro')}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DarijaDecoder;
